import { useEffect, useState, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ServiceNode as ServiceNodeType, ImpactAnalysis, dependenciesApi } from '@/lib/dependencies';
import { AlertCircle, Database, Server, ExternalLink } from 'lucide-react';
import { useServicesStore } from '@/stores/servicesStore';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { useServiceHealthStore } from '@/stores/serviceHealthStore';
import { ServiceHealthStatus } from '@/types/api';
import dagre from 'dagre';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Tabs, TabsContent, TabsList, TabsTrigger, useToast, ErrorAlert } from '@nimbitlabs/ui';

// Custom node component
const CustomNode = ({ data }: any) => {
  const getStatusColor = () => {
    if (data.isHovered) {
      return 'border-red-500 bg-red-500/20 shadow-2xl shadow-red-500/50';
    }
    if (data.isInBlastRadius) {
      return 'border-red-400 bg-red-400/10 shadow-lg shadow-red-400/30';
    }

    // Base color driven by service type so node and badge match
    switch (data.type) {
      case 'service':
        return 'border-blue-500 bg-blue-500/10';
      case 'database':
        return 'border-purple-500 bg-purple-500/10';
      case 'external':
        return 'border-orange-500 bg-orange-500/10';
      default:
        return 'border-border';
    }
  };

  const getTypeBadgeColor = () => {
    switch (data.type) {
      case 'service':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'database':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
      case 'external':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getHealthBadgeColor = () => {
    const normalizedStatus = data.status?.toLowerCase();
    switch (normalizedStatus) {
      case 'healthy':
        return 'bg-green-500/20 text-green-600 border-green-500/40 dark:bg-green-500/20 dark:text-green-400 dark:border-green-500/40';
      case 'degraded':
        return 'bg-yellow-500/20 text-yellow-600 border-yellow-500/40 dark:bg-yellow-500/20 dark:text-yellow-400 dark:border-yellow-500/40';
      case 'unhealthy':
      case 'timeout':
      case 'dnsfailure':
        return 'bg-red-500/15 text-red-600 border-red-500/30 dark:text-red-400';
      default:
        return 'bg-orange-500/15 text-orange-600 border-orange-500/30 dark:text-orange-400';
    }
  };

  const getHealthLabel = () => {
    const normalizedStatus = data.status?.toLowerCase();
    switch (normalizedStatus) {
      case 'healthy':
        return '● Healthy';
      case 'degraded':
        return '● Degraded';
      case 'unhealthy':
      case 'timeout':
      case 'dnsfailure':
        return '● Unhealthy';
      default:
        return '● Unknown';
    }
  };

  const getIcon = () => {
    switch (data.type) {
      case 'service':
        return <Server className="h-4 w-4" />;
      case 'database':
        return <Database className="h-4 w-4" />;
      case 'external':
        return <ExternalLink className="h-4 w-4" />;
      default:
        return <Server className="h-4 w-4" />;
    }
  };


  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 ${getStatusColor()} bg-card shadow-lg min-w-[200px] max-w-[220px] cursor-pointer hover:shadow-xl transition-all duration-200`}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3" />
      <Handle type="source" position={Position.Bottom} className="w-3 h-3" />
      
      <div className="flex items-center gap-2 mb-2">
        {getIcon()}
        <div className="font-semibold text-sm truncate">{data.label}</div>
      </div>
      
      <div className="flex flex-wrap gap-1.5 mb-2">
        <Badge className={`text-xs border ${getTypeBadgeColor()}`}>
          {data.type}
        </Badge>
        <Badge className={`text-xs border ${getHealthBadgeColor()}`}>
          {getHealthLabel()}
        </Badge>
      </div>

      {data.isInBlastRadius && (
        <div className="mt-2 pt-2 border-t border-red-500/30 text-xs text-red-400 font-medium flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          <span>Affected</span>
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

// Hierarchical layout using dagre for better dependency visualization
const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  if (nodes.length === 0) return { nodes, edges };

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  
  // Configure layout for a more circular/radial arrangement with increased spacing
  dagreGraph.setGraph({ 
    rankdir: 'TB',  // Top to bottom for more compact circular feel
    nodesep: 90,    // Horizontal spacing between nodes (increased from 60)
    ranksep: 140,   // Vertical spacing between ranks (increased from 100)
    marginx: 50,
    marginy: 50,
    ranker: 'tight-tree',  // Tighter tree algorithm for rounded shape
  });

  // Add nodes to dagre with proper dimensions
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 220, height: 120 });
  });

  // Add edges to dagre
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply positions to nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 110, // Center the node (width/2)
        y: nodeWithPosition.y - 75, // Center the node (height/2)
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

export default function DependencyGraph() {
  const [serviceNodes, setServiceNodes] = useState<ServiceNodeType[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [blastRadius, setBlastRadius] = useState<Set<string>>(new Set());
  const [upstreamDeps, setUpstreamDeps] = useState<Set<string>>(new Set());
  const [impactAnalysis, setImpactAnalysis] = useState<ImpactAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string>('');
  const { toast } = useToast();
  const { services, fetchServices } = useServicesStore();
  const { environments, fetchEnvironments } = useEnvironmentsStore();
  const { healthData, getHealthByService } = useServiceHealthStore();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([
      fetchEnvironments(),
      loadGraph()
    ]);
  };

  // Set initial environment when environments load
  useEffect(() => {
    if (environments.length > 0 && !selectedEnvironmentId) {
      // Sort environments by tier
      const sortedEnvs = [...environments].sort((a, b) => {
        const tierOrder = ['development', 'dev', 'staging', 'uat', 'qa', 'test', 'production', 'prod'];
        const getTier = (name: string) => {
          const lowerName = name.toLowerCase();
          const index = tierOrder.findIndex(tier => lowerName.includes(tier));
          return index === -1 ? 999 : index;
        };
        return getTier(a.name) - getTier(b.name);
      });
      setSelectedEnvironmentId(sortedEnvs[0].id);
    }
  }, [environments, selectedEnvironmentId]);

  // Update node health status when environment changes
  useEffect(() => {
    if (selectedEnvironmentId && serviceNodes.length > 0) {
      setNodes(currentNodes =>
        currentNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            status: getServiceHealthStatus(node.id),
          },
        }))
      );
    }
  }, [selectedEnvironmentId, healthData, serviceNodes, setNodes]);

  // Get health status for a service in the selected environment
  const getServiceHealthStatus = (serviceId: string): string => {
    if (!selectedEnvironmentId) return 'unknown';
    
    const healthChecks = getHealthByService(serviceId).filter(
      h => h.environmentId === selectedEnvironmentId
    );
    
    if (healthChecks.length === 0) {
      return 'unknown';
    }
    
    // If any are unhealthy/timeout/dnsfailure, show unhealthy
    if (healthChecks.some(h => 
      h.status === ServiceHealthStatus.Unhealthy || 
      h.status === ServiceHealthStatus.Timeout || 
      h.status === ServiceHealthStatus.DnsFailure
    )) {
      return 'unhealthy';
    }
    
    // If any are degraded, show degraded
    if (healthChecks.some(h => h.status === ServiceHealthStatus.Degraded)) {
      return 'degraded';
    }
    
    // If all are healthy, show healthy
    if (healthChecks.every(h => h.status === ServiceHealthStatus.Healthy)) {
      return 'healthy';
    }
    
    return 'unknown';
  };

  const loadGraph = async () => {
    try {
      setLoading(true);
      
      // Fetch both dependency graph and services data
      const [graph] = await Promise.all([
        dependenciesApi.getDependencyGraph(),
        fetchServices()
      ]);
      
      setServiceNodes(graph.nodes);

      // Convert to React Flow format with health status
      const flowNodes: Node[] = graph.nodes.map((node) => {
        const healthStatus = getServiceHealthStatus(node.id);
        return {
          id: node.id,
          type: 'custom',
          data: {
            label: node.name,
            type: node.type,
            status: healthStatus,
            isHovered: false,
            isInBlastRadius: false,
          },
          position: { x: 0, y: 0 }, // Will be set by dagre
        };
      });

      console.log('=== DEPENDENCY GRAPH DEBUG ===');
      console.log('API Response:', JSON.stringify({ nodes: graph.nodes.length, edges: graph.edges.length }, null, 2));
      console.log('Graph edges from API:', JSON.stringify(graph.edges, null, 2));

      // Enhanced edge styling with gradients and better visibility
      const getEdgeColor = (type: string) => {
        switch (type) {
          case 'api': return '#3b82f6'; // Blue
          case 'database': return '#8b5cf6'; // Purple
          case 'message-queue': return '#f59e0b'; // Amber
          case 'cache': return '#10b981'; // Green
          default: return '#64748b'; // Slate
        }
      };

      const flowEdges: Edge[] = graph.edges.map((edge, index) => {
        const color = getEdgeColor(edge.type);
        return {
          id: `${edge.source}-${edge.target}-${index}`,
          source: edge.source,
          target: edge.target,
          type: 'default',  // Using default for cleaner straight lines
          animated: true,
          label: edge.type,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: color,
            width: 24,
            height: 24,
          },
          style: {
            stroke: color,
            strokeWidth: 3,
            strokeDasharray: '5,5',
            filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.3))',
          },
          labelStyle: { 
            fill: 'hsl(var(--foreground))', 
            fontSize: 12, 
            fontWeight: 600,
          } as React.CSSProperties,
          labelBgStyle: { 
            fill: 'hsl(var(--card))', 
            fillOpacity: 0.98, 
            padding: 6,
            borderRadius: 6,
            stroke: color,
            strokeWidth: 1,
            strokeOpacity: 0.3,
          },
          data: { isInBlastRadius: false },
        };
      });

      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
        flowNodes,
        flowEdges
      );
      
      console.log('React Flow Edges:', JSON.stringify(layoutedEdges, null, 2));
      console.log('=== END DEBUG ===');

      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate blast radius - all nodes affected if this node fails (downstream impact)
  const calculateBlastRadius = useCallback((nodeId: string, currentEdges: Edge[]): Set<string> => {
    const affected = new Set<string>();
    const queue = [nodeId];
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      
      // Find all nodes that depend on the current node (where current is the target)
      currentEdges.forEach(edge => {
        if (edge.target === current && !affected.has(edge.source)) {
          affected.add(edge.source);
          queue.push(edge.source);
        }
      });
    }
    
    return affected;
  }, []);

  // Calculate upstream dependencies - all nodes this node depends on
  const calculateUpstreamDeps = useCallback((nodeId: string, currentEdges: Edge[]): Set<string> => {
    const dependencies = new Set<string>();
    const queue = [nodeId];
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      
      // Find all nodes that the current node depends on (where current is the source)
      currentEdges.forEach(edge => {
        if (edge.source === current && !dependencies.has(edge.target)) {
          dependencies.add(edge.target);
          queue.push(edge.target);
        }
      });
    }
    
    return dependencies;
  }, []);

  const handleNodeMouseEnter = useCallback((_event: any, node: Node) => {
    setHoveredNode(node.id);
    const radius = calculateBlastRadius(node.id, edges);
    setBlastRadius(radius);
  }, [edges, calculateBlastRadius]);

  const handleNodeMouseLeave = useCallback(() => {
    setHoveredNode(null);
    setBlastRadius(new Set());
  }, []);

  const handleNodeClick = useCallback(async (_event: any, node: Node) => {
    // Toggle selection - if clicking the same node, deselect it
    if (selectedNode === node.id) {
      setSelectedNode(null);
      setUpstreamDeps(new Set());
      setImpactAnalysis(null);
      return;
    }
    
    setSelectedNode(node.id);
    const upstream = calculateUpstreamDeps(node.id, edges);
    setUpstreamDeps(upstream);
    
    try {
      const analysis = await dependenciesApi.getImpactAnalysis(node.id);
      setImpactAnalysis(analysis);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load impact analysis',
        variant: 'destructive',
      });
    }
  }, [toast, edges, calculateUpstreamDeps, selectedNode]);

  // Update node data with hover/selection state
  const nodesWithBlastRadius = nodes.map(node => ({
    ...node,
    data: {
      ...node.data,
      isHovered: node.id === hoveredNode || node.id === selectedNode,
      isInBlastRadius: blastRadius.has(node.id) || upstreamDeps.has(node.id),
    },
  }));

  // Update edge visibility and styles based on hover/selection
  const edgesWithBlastRadius = edges.map(edge => {
    // HOVER: Show downstream impact (what depends on hovered node)
    const isDownstreamEdge = hoveredNode && (
      (edge.target === hoveredNode && blastRadius.has(edge.source)) ||
      (blastRadius.has(edge.target) && blastRadius.has(edge.source))
    );
    
    // CLICK: Show upstream dependencies (what selected node depends on)
    const isUpstreamEdge = selectedNode && (
      (edge.source === selectedNode && upstreamDeps.has(edge.target)) ||
      (upstreamDeps.has(edge.source) && upstreamDeps.has(edge.target))
    );
    
    const isHighlighted = isDownstreamEdge || isUpstreamEdge;
    
    return {
      ...edge,
      animated: isHighlighted,
      style: isHighlighted
        ? {
            stroke: isDownstreamEdge ? '#ef4444' : '#3b82f6',
            strokeWidth: 4,
            opacity: 1,
            filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.4))',
          }
        : {
            stroke: '#64748b',
            strokeWidth: 2,
            opacity: 0.3,
            strokeDasharray: '5,5',
          },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: isDownstreamEdge ? '#ef4444' : isUpstreamEdge ? '#3b82f6' : '#64748b',
        width: isHighlighted ? 28 : 20,
        height: isHighlighted ? 28 : 20,
      },
      labelStyle: isHighlighted ? {
        fill: 'hsl(var(--foreground))',
        fontSize: 12,
        fontWeight: 700,
      } as React.CSSProperties : {
        fill: 'hsl(var(--muted-foreground))',
        fontSize: 10,
        fontWeight: 500,
      } as React.CSSProperties,
      labelBgStyle: isHighlighted ? {
        fill: isDownstreamEdge ? '#ef4444' : '#3b82f6',
        fillOpacity: 0.2,
        padding: 6,
        borderRadius: 6,
        stroke: isDownstreamEdge ? '#ef4444' : '#3b82f6',
        strokeWidth: 2,
        strokeOpacity: 0.5,
      } : {
        fill: 'hsl(var(--card))',
        fillOpacity: 0.8,
        padding: 4,
        borderRadius: 4,
      },
    };
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading dependency graph...</div>
      </div>
    );
  }

  // Sort environments by tier (development -> UAT -> production)
  const sortedEnvironments = [...environments].sort((a, b) => {
    const tierOrder = ['development', 'dev', 'staging', 'uat', 'qa', 'test', 'production', 'prod'];
    const getTier = (name: string) => {
      const lowerName = name.toLowerCase();
      const index = tierOrder.findIndex(tier => lowerName.includes(tier));
      return index === -1 ? 999 : index;
    };
    return getTier(a.name) - getTier(b.name);
  });

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      <ErrorAlert error={error} />
      <div className="flex flex-col gap-4 mb-3 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Service Dependencies</h1>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 text-xs overflow-x-auto pb-2 w-full">
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <Server className="h-3 w-3 text-blue-400" />
              <span className="text-muted-foreground">Service</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Database className="h-3 w-3 text-purple-400" />
              <span className="text-muted-foreground">Database</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ExternalLink className="h-3 w-3 text-orange-400" />
              <span className="text-muted-foreground">External</span>
            </div>
          </div>
          <div className="hidden sm:block h-4 w-px bg-border" />
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div className="h-0.5 w-4 bg-red-500" />
              <span className="text-muted-foreground">Hover: Impact</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-0.5 w-4 bg-blue-500" />
              <span className="text-muted-foreground">Click: Dependencies</span>
            </div>
          </div>
        </div>
      </div>

      {sortedEnvironments.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No environments found</h3>
            <p className="text-muted-foreground text-center">
              Create an environment first to view dependencies
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={selectedEnvironmentId} onValueChange={setSelectedEnvironmentId} className="space-y-6">
          <div className="w-full overflow-x-auto py-3 px-1 sm:px-0">
            <div className="inline-flex w-max min-w-full md:min-w-0 rounded-xl bg-card shadow-soft p-[4px]">
              <TabsList className="inline-flex h-12 items-center justify-start gap-2 rounded-xl bg-gradient-to-r from-muted/40 via-muted/30 to-muted/40 p-1.5 backdrop-blur-sm w-full">
                {sortedEnvironments.map((env) => (
                  <TabsTrigger 
                    key={env.id} 
                    value={env.id}
                    className="relative overflow-hidden data-[state=active]:text-primary data-[state=active]:scale-[1.08] data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/40 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:scale-[1.02] px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] before:absolute before:inset-0 before:bg-primary/10 before:rounded-lg before:scale-75 before:opacity-0 before:rotate-[-2deg] data-[state=active]:before:scale-100 data-[state=active]:before:opacity-100 data-[state=active]:before:rotate-0 before:transition-all before:duration-500 before:ease-[cubic-bezier(0.34,1.56,0.64,1)] after:absolute after:inset-0 after:shadow-[0_0_20px_rgba(var(--primary-rgb),0.3)] after:rounded-lg after:scale-75 after:opacity-0 data-[state=active]:after:scale-100 data-[state=active]:after:opacity-100 after:transition-all after:duration-500 after:ease-[cubic-bezier(0.34,1.56,0.64,1)]"
                  >
                    <span className="relative z-10">{env.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </div>

          {sortedEnvironments.map((env) => (
            <TabsContent key={env.id} value={env.id} className="mt-6">
              <Card>
                <CardContent className="p-0">
          <div className="h-[400px] sm:h-[500px] md:h-[700px] rounded-lg bg-muted/10">
                <ReactFlow
                  nodes={nodesWithBlastRadius}
                  edges={edgesWithBlastRadius}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={handleNodeClick}
                  onNodeMouseEnter={handleNodeMouseEnter}
                  onNodeMouseLeave={handleNodeMouseLeave}
                  nodeTypes={nodeTypes}
                  fitView
                  attributionPosition="bottom-left"
                  defaultEdgeOptions={{
                    type: 'smoothstep',
                    animated: true,
                  }}
                  minZoom={0.2}
                  maxZoom={2}
                >
                  <Background color="hsl(var(--muted-foreground))" gap={16} />
                  <Controls />
                  <MiniMap
                    nodeColor={(node) => {
                      switch (node.data.status) {
                        case 'healthy':
                          return '#22c55e';
                        case 'degraded':
                          return '#eab308';
                        case 'down':
                          return '#ef4444';
                        default:
                          return '#6b7280';
                      }
                    }}
                  />
                </ReactFlow>
              </div>
            </CardContent>
          </Card>

          {selectedNode && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-500" />
                    <CardTitle>Upstream Dependencies</CardTitle>
                  </div>
                  <Badge variant="outline" className="text-blue-500 border-blue-500">
                    {upstreamDeps.size} dependencies
                  </Badge>
                </div>
                <CardDescription>
                  Services that {serviceNodes.find(n => n.id === selectedNode)?.name || 'this service'} depends on
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {upstreamDeps.size > 0 ? (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Required Services</h4>
                    <div className="space-y-1">
                      {Array.from(upstreamDeps).map((id) => {
                        const node = serviceNodes.find((n) => n.id === id);
                        return (
                          <div key={id} className="text-sm text-muted-foreground flex items-center gap-2">
                            <div className="h-1 w-1 rounded-full bg-blue-500" />
                            {node?.name || id}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    This service has no dependencies
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {impactAnalysis && selectedNode && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-warning" />
                    <CardTitle>Downstream Impact</CardTitle>
                  </div>
                  <Badge variant="outline" className="text-red-500 border-red-500">
                    {impactAnalysis.totalImpact} affected
                  </Badge>
                </div>
                <CardDescription>
                  Services that would be affected if {impactAnalysis.serviceName} fails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-2">Total Impact</h4>
                  <div className="text-3xl font-bold text-primary">{impactAnalysis.totalImpact}</div>
                  <p className="text-xs text-muted-foreground mt-1">services affected</p>
                </div>

                {impactAnalysis.directDependents.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">
                      Direct Dependents ({impactAnalysis.directDependents.length})
                    </h4>
                    <div className="space-y-1">
                      {impactAnalysis.directDependents.map((id) => {
                        const node = serviceNodes.find((n) => n.id === id);
                        return (
                          <div key={id} className="text-sm text-muted-foreground">
                            • {node?.name || id}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {impactAnalysis.indirectDependents.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">
                      Indirect Dependents ({impactAnalysis.indirectDependents.length})
                    </h4>
                    <div className="space-y-1">
                      {impactAnalysis.indirectDependents.slice(0, 5).map((id) => {
                        const node = serviceNodes.find((n) => n.id === id);
                        return (
                          <div key={id} className="text-sm text-muted-foreground">
                            • {node?.name || id}
                          </div>
                        );
                      })}
                      {impactAnalysis.indirectDependents.length > 5 && (
                        <div className="text-xs text-muted-foreground">
                          +{impactAnalysis.indirectDependents.length - 5} more
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    If this service goes down, {impactAnalysis.totalImpact} other service
                    {impactAnalysis.totalImpact !== 1 ? 's' : ''} could be affected.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      ))}
    </Tabs>
  )}
    </div>
  );
}
